import {
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  ListItem,
  MenuItem,
  Select,
  Switch,
  TextField,
  Tooltip,
} from "@mui/material";
import CoreButtonHoverIcon from "../../../../app/core/components/CoreButtonIcon/CoreButtonHoverIcon";
import AddCircleOutlineRoundedIcon from "@mui/icons-material/AddCircleOutlineRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from "@mui/icons-material/Close";
import { useEffect, useState } from "react";
import { t } from "i18next";
import { toast } from "react-toastify";
import CoreDialog from "../../../../app/core/components/CoreDialog/CoreDialog";
import { SkeletonsHorizontalLine } from "../../../common/LoadingStuff/Skeletons/SkeletonsHorizontalLine";
import {
  useDeleteMainCategoryMutation,
  useEditMacroCategoryMutation,
  useNewCategoryMutation,
  useNewMainCategoryMutation,
} from "../../../api/admin/categoriesApi";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";
import { useSelector } from "react-redux";
import StarsRoundedIcon from "@mui/icons-material/StarsRounded";

const header = () => {
  return (
    <div className="bg-zinc-50 flex p-2 px-2">
      <p className="w-2/3">{t("CategoryName")}</p>
      <p className="text-center w-1/6">{t("Keywords")}</p>
      <p className="text-center w-1/6">{t("Queries")}</p>
    </div>
  );
};

const CategoriesList = ({
  macroCategories,
  categoriesList,
  selectedCategory,
  setSelectedCategory,
  isCategoryChanged,
  setIsCategoryChanged,
  showPermanentCategories,
  setShowPermanentCategories,
  categoryInputValue,
  setCategoryInputValue,
}) => {
  const primaryColor = useSelector((state) => state.app.primaryColor);
  const [newCategory, setNewCategory] = useState("");
  const [selectedMacroCategory, setSelectedMacroCategory] = useState("");
  const [openExitWithoutSaveDialog, setOpenExitWithoutSaveDialog] =
    useState(false);
  const [callBackForDialog, setCallBackForDialog] = useState(() => {});
  const [macroCategoryToEdit, setMacroCategoryToEdit] = useState("");
  const [macroCategoryNewName, setMacroCategoryNewName] = useState("");

  useEffect(() => {
    if (macroCategoryToEdit) {
      setMacroCategoryNewName(
        macroCategories.find((e) => e.value === macroCategoryToEdit)?.label
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [macroCategoryToEdit]);

  const [addNewCategory] = useNewCategoryMutation();
  const [editMacroCategory, { isLoading: isEditingMacroCategoryName }] =
    useEditMacroCategoryMutation();
  const [addNewMacroCategory, { isLoading: isAddingNewMacroCategory }] =
    useNewMainCategoryMutation();
  const [deleteMainCategory, { isLoading: isDeletingMacroCategory }] =
    useDeleteMainCategoryMutation();

  const handleAddCategory = () => {
    const id = toast.loading(t("Creation..."), {
      type: "info",
    });
    const newCategoryObj = {
      name: newCategory,
      description: "",
      mainCategoryId: selectedMacroCategory,
    };
    addNewCategory(newCategoryObj)
      .then((res) => {
        if (res.data.Result !== 1)
          return toast.update(id, {
            render: t("CategoryNotCreated"),
            type: "error",
            autoClose: 1500,
            isLoading: false,
          });
        setNewCategory("");
        toast.update(id, {
          render: t("CategoryCreated"),
          type: "success",
          autoClose: 1500,
          isLoading: false,
        });
      })
      .catch(() => {
        toast.update(id, {
          render: t("CategoryNotCreated"),
          type: "error",
          autoClose: 1500,
          isLoading: false,
        });
      });
  };

  const handleAddMacroCategory = async () => {
    const id = toast.loading(t("Creating..."), {
      type: "info",
    });
    try {
      const data = { name: macroCategoryNewName };
      await addNewMacroCategory(data);
      toast.update(id, {
        render: "Macro" + t("CategoryCreated").toLowerCase(),
        type: "success",
        autoClose: 1500,
        isLoading: false,
      });
      setMacroCategoryToEdit("");
      setMacroCategoryNewName("");
    } catch (error) {
      toast.update(id, {
        render: "Macro" + t("CategoryNotCreated").toLowerCase(),
        type: "error",
        autoClose: 1500,
        isLoading: false,
      });
    }
  };

  const handleEditMacroCategory = async () => {
    const id = toast.loading(t("Deleting..."), {
      type: "info",
    });
    try {
      const data = { id: macroCategoryToEdit, name: macroCategoryNewName };
      await editMacroCategory(data);
      toast.update(id, {
        render: "Macro" + t("CategoryUpdated").toLowerCase(),
        type: "success",
        autoClose: 1500,
        isLoading: false,
      });
      setMacroCategoryToEdit("");
      setMacroCategoryNewName("");
    } catch (error) {
      toast.update(id, {
        render: "Macro" + t("CategoryNotUpdated").toLowerCase(),
        type: "error",
        autoClose: 1500,
        isLoading: false,
      });
    }
  };

  const handleDeleteMacroCategory = async (mainCategoryId) => {
    const id = toast.loading(t("Deleting..."), {
      type: "info",
    });
    try {
      const data = await deleteMainCategory(mainCategoryId);
      if (data.data.Result !== 1) throw new Error(data.data.Exception);
      toast.update(id, {
        render: "Macro" + t("CategoryDeleted").toLowerCase(),
        type: "success",
        autoClose: 1500,
        isLoading: false,
      });
    } catch (error) {
      console.error(error);
      toast.update(id, {
        render: "Macro" + t("CategoryNotDeleted").toLowerCase(),
        type: "error",
        autoClose: 1500,
        isLoading: false,
      });
    }
  };

  const handleExit = () => {
    setIsCategoryChanged(false);
    setOpenExitWithoutSaveDialog(false);
    callBackForDialog();
  };

  return (
    <div
      style={{
        border: "1px solid #0000001f",
      }}
      className="xl:w-1/2 w-full p-2 rounded-md shadow-md"
    >
      <div className="flex justify-between items-center m-3">
        <div className="w-1/2 flex items-center gap-8">
          <TextField
            className="w-full"
            label={t("SearchCategory")}
            value={categoryInputValue}
            onChange={(e) => setCategoryInputValue(e.target.value)}
            size="small"
          />
        </div>

        <FormControlLabel
          control={
            <Switch
              color="primary"
              checked={showPermanentCategories}
              onChange={(e) => setShowPermanentCategories(e.target.checked)}
            />
          }
          label={t("ShowPermanentCategories")}
          labelPlacement="start"
        />
      </div>
      <Divider />
      {header()}

      <div className="w-full flex flex-col gap-5">
        <div
          style={{
            border: "1px solid #0000001f",
            height: "45vh",
          }}
          className="w-full rounded-md overflow-auto"
        >
          {!categoriesList && (
            <div className="p-4">
              <SkeletonsHorizontalLine height={50} length={25} />
            </div>
          )}
          {categoriesList?.map((categoryObj, index) => (
            <div key={index}>
              <ListItem
                selected={categoryObj?.id === selectedCategory?.id}
                className={`${
                  categoryObj?.id === selectedCategory?.id ? "bg-grey/70" : ""
                }`}
                key={categoryObj.name}
                button={categoryObj.categoryType === "DynamicForm"}
                onClick={() => {
                  if (categoryObj.categoryType !== "DynamicForm") return;
                  if (isCategoryChanged) {
                    setOpenExitWithoutSaveDialog(true);
                    setCallBackForDialog(
                      () => () => setSelectedCategory(categoryObj)
                    );
                    return;
                  }
                  setSelectedCategory(categoryObj);
                }}
              >
                <div className="w-2/3 flex items-center gap-4">
                  {categoryObj.categoryType === "DynamicForm" && (
                    <Tooltip title={"Classificatore LLM"}>
                      <SmartToyOutlinedIcon
                        style={{ color: primaryColor, zIndex: 20 }}
                      />
                    </Tooltip>
                  )}
                  <div className="w-full text-black font-bold overflow-hidden">
                    {categoryObj.name}
                  </div>
                  <Chip
                    variant="outlined"
                    label={categoryObj.mainCategoryName}
                    size="small"
                  />

                  {categoryObj.isDefault && (
                    <Chip
                      style={{
                        color: primaryColor,
                        borderColor: primaryColor,
                      }}
                      variant="outlined"
                      label="Default"
                      size="small"
                      icon={<StarsRoundedIcon color={primaryColor} />}
                    />
                  )}
                </div>
                <p className="text-center w-1/6">
                  {categoryObj.categoryType === "DynamicForm"
                    ? categoryObj?.keywordsCount
                    : "-"}
                </p>
                <p className="text-center w-1/6">
                  {categoryObj.categoryType === "DynamicForm"
                    ? categoryObj?.queriesCount
                    : "-"}
                </p>
              </ListItem>
              <Divider />
            </div>
          ))}
        </div>

        <div
          style={{ height: "7.5vh" }}
          className="flex items-center gap-4 px-4"
        >
          {macroCategoryToEdit ? (
            <div className="w-full items-center flex gap-1">
              <TextField
                value={macroCategoryNewName}
                onChange={(e) => setMacroCategoryNewName(e.target.value)}
              />
              <Tooltip title={`${t("Save")}`}>
                <IconButton
                  onClick={handleEditMacroCategory}
                  className="text-green-700"
                >
                  {isEditingMacroCategoryName ? (
                    <CircularProgress size={16} />
                  ) : (
                    <CheckRoundedIcon className={`fill-green-700`} />
                  )}
                </IconButton>
              </Tooltip>
              <Tooltip title={`${t("Save")}`}>
                <IconButton
                  onClick={() => setMacroCategoryToEdit("")}
                  className="text-blue-900"
                >
                  <CloseIcon className={`fill-blue-900`} />
                </IconButton>
              </Tooltip>
            </div>
          ) : (
            <FormControl fullWidth>
              <InputLabel id="demo-simple-select-label">
                {t("MacroCategory")}
              </InputLabel>
              <Select
                className="w-full flex items-center"
                value={selectedMacroCategory}
                onChange={(e) => {
                  if (e.target.value === "new") return handleAddMacroCategory();
                  setSelectedMacroCategory(e.target.value);
                }}
                label={t("MacroCategory")}
                renderValue={(value) =>
                  macroCategories.find((category) => category.value === value)
                    ?.label
                }
              >
                {macroCategories.map((category) => (
                  <MenuItem key={category.value} value={category.value}>
                    <div className="w-full flex justify-between items-center gap-2">
                      {category.label}
                      <div className="flex justify-end items-center gap-2">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            setMacroCategoryToEdit(category.value);
                          }}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteMacroCategory(category.value);
                          }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </div>
                    </div>
                  </MenuItem>
                ))}
                <div className="w-full flex m-3 items-center">
                  <TextField
                    className="w-4/5"
                    placeholder={t("NewMainCategory")}
                    value={macroCategoryNewName}
                    onChange={(e) => setMacroCategoryNewName(e.target.value)}
                    onKeyDown={(e) => e.stopPropagation()}
                    size="small"
                  />
                  <IconButton
                    onClick={handleAddMacroCategory}
                    disabled={isAddingNewMacroCategory}
                  >
                    <Tooltip title={t("NewMainCategory")}>
                      <AddCircleOutlineRoundedIcon />
                    </Tooltip>
                  </IconButton>
                </div>
              </Select>
            </FormControl>
          )}
          <TextField
            disabled={!selectedMacroCategory}
            fullWidth
            label={t("NewCategory")}
            value={newCategory}
            onChange={(e) => setNewCategory(e.target.value)}
            placeholder={t("InsertCategoryName")}
          />
          <CoreButtonHoverIcon
            disabled={!selectedMacroCategory}
            className="m-1"
            icon={<AddCircleOutlineRoundedIcon fontSize="small" />}
            testo={t("Add")}
            onClick={handleAddCategory}
          />
        </div>
      </div>
      <CoreDialog
        open={openExitWithoutSaveDialog}
        closeDialog={() => setOpenExitWithoutSaveDialog(false)}
        title={t("EditNotSavedShort")}
        content={t("EditNotSavedLong")}
        positiveButton={t("LeaveAnyway")}
        negativeButton={t("Cancel")}
        handlePositive={handleExit}
      />
    </div>
  );
};

export default CategoriesList;
